import type { PillColor } from '@/shared/components/Pill';
import type { AnyTranslationKey } from '@/shared/i18n';
import type { UserStatus } from './admin.schema';
import { formatDate } from '@/shared/lib/format';

export const formatAdminDate = (value: string | null | undefined, notProvidedLabel: string) =>
  value ? formatDate(value) : notProvidedLabel;

export const userStatusColor = (status: UserStatus): PillColor => {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'deactivated':
      return 'neutral';
  }
};

export const canonicalAuditAction = (action: string): string =>
  action.replace(/\s\/(admin|manager)(?=\/)/, ' ');

export const actionLabelKeys: Record<string, AnyTranslationKey> = {
  'GET /users': 'userManagement:auditLog.actions.viewUsers',
  'GET /users/stats': 'userManagement:auditLog.actions.viewUserStats',
  'GET /users/detail': 'userManagement:auditLog.actions.viewUserDetail',
  'GET /audit-logs': 'userManagement:auditLog.actions.viewAuditLogs',
  'POST /users': 'userManagement:auditLog.actions.createUser',
  'PATCH /users': 'userManagement:auditLog.actions.updateUser',
  'DELETE /users': 'userManagement:auditLog.actions.deleteUser',
  'POST /users/subscriptions': 'userManagement:auditLog.actions.assignSubscription',
  'POST /subscriptions': 'userManagement:auditLog.actions.createSubscription',
  'DELETE /subscriptions': 'userManagement:auditLog.actions.deleteSubscription',
  'POST /subscriptions/extend': 'userManagement:auditLog.actions.extendSubscription',
  'DELETE /subscriptions/extend': 'userManagement:auditLog.actions.deleteSubscriptionExtension',
};

export const formatAuditAction = (
  action: string,
  t: (key: AnyTranslationKey) => string,
): string => {
  const canonical = canonicalAuditAction(action);
  const labelKey = actionLabelKeys[canonical];
  if (labelKey) {
    return t(labelKey);
  }
  const verb: Record<string, string> = {
    GET: 'View',
    POST: 'Create',
    PATCH: 'Update',
    PUT: 'Update',
    DELETE: 'Delete',
  };
  const [method = '', path] = canonical.split(' ');
  const label = verb[method] ?? method;
  if (!path) {
    return label;
  }
  const parts = path.split('/').filter(Boolean);
  return `${label} ${parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')}`;
};

export const auditStatusColor = (status: number): PillColor => {
  if (status >= 200 && status < 300) {
    return 'success';
  }
  if (status >= 400 && status < 500) {
    return 'warning';
  }
  if (status >= 500) {
    return 'danger';
  }
  return 'neutral';
};

export const auditStatusLabel = (status: number): string => {
  if (status >= 200 && status < 300) {
    return `${status} OK`;
  }
  const labels: Record<number, string> = {
    400: '400 Bad Request',
    401: '401 Unauthorized',
    403: '403 Forbidden',
    404: '404 Not Found',
    409: '409 Conflict',
    422: '422 Unprocessable',
    429: '429 Too Many Requests',
    500: '500 Server Error',
    502: '502 Bad Gateway',
    503: '503 Unavailable',
  };
  return labels[status] ?? String(status);
};

const parseBrowser = (ua: string): string => {
  if (ua.includes('Firefox/')) {
    return 'Firefox';
  }
  if (ua.includes('Edg/')) {
    return 'Edge';
  }
  if (ua.includes('OPR/') || ua.includes('Opera')) {
    return 'Opera';
  }
  if (ua.includes('Chrome/')) {
    return 'Chrome';
  }
  if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    return 'Safari';
  }
  return 'Unknown';
};

const parseOS = (ua: string): string => {
  if (ua.includes('Windows')) {
    return 'Windows';
  }
  if (ua.includes('Mac OS X')) {
    return 'macOS';
  }
  if (ua.includes('Android')) {
    return 'Android';
  }
  if (ua.includes('iPhone') || ua.includes('iPad')) {
    return 'iOS';
  }
  if (ua.includes('Linux')) {
    return 'Linux';
  }
  return 'Unknown';
};

export const formatUserAgent = (ua: string | null | undefined, unknownLabel: string): string => {
  if (!ua) {
    return unknownLabel;
  }
  const os = parseOS(ua);
  const browser = parseBrowser(ua);
  if (os === 'Unknown' && browser === 'Unknown') {
    return unknownLabel;
  }
  const osLabel = os === 'Unknown' ? unknownLabel : os;
  const browserLabel = browser === 'Unknown' ? unknownLabel : browser;
  return `${browserLabel} · ${osLabel}`;
};
